import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Users,
  Repeat,
  Layers,
  Archive,
  Sparkles,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { useAuth } from "@core/contexts/AuthContext";
import { useSoloTrial } from "@core/hooks/useSoloTrial";

// ORDRE ET HIÉRARCHIE, revus le 18/09 après lecture réelle de la page.
//
// Deux défauts, et le premier n'était pas un défaut d'écriture mais de
// classement : les cinq avantages arrivaient dans l'ordre où ils avaient été
// codés, l'IA en DERNIER. Or c'est le point de contact le plus fréquent du
// produit — celui par lequel un nouvel inscrit commence — et le seul dont
// l'essai change immédiatement l'expérience. Les groupes viennent ensuite :
// c'est le vrai différenciateur de Solo, celui qui fait revenir.
//
// Le second : cinq cartes de même poids visuel se lisent comme un formulaire,
// pas comme une promesse. On distingue donc ce qu'on met en avant de ce qu'on
// mentionne. Les deux derniers (plafond de sondages actifs, rétention) sont des
// levées de contrainte, pas des fonctionnalités : ils se disent en une ligne.
const AVANTAGES_PHARES = [
  { id: "ai", icon: Sparkles },
  { id: "groups", icon: Users },
  { id: "recurring", icon: Repeat },
] as const;

const AVANTAGES_SECONDAIRES = [
  { id: "active_polls", icon: Layers },
  { id: "retention", icon: Archive },
] as const;

/**
 * Écran de démarrage de l'essai Solo (14 jours, sans carte bancaire).
 *
 * C'est le seul endroit du parcours qui couvre à la fois l'inscription par
 * e-mail et l'inscription Google : celle-ci ne passe par aucun formulaire, donc
 * une case posée sur le formulaire d'inscription n'aurait couvert que la
 * moitié des comptes. D'où la case d'opt-in marketing ici (décision 4), NON
 * pré-cochée et séparée du bouton — cocher n'est pas la condition pour obtenir
 * l'essai, et le bouton ne doit pas se lire comme un consentement.
 *
 * Trois choses que cet écran ne fait volontairement pas :
 * - il n'annonce aucun nombre de messages IA, seulement des conversations
 *   (décision 16 : le compteur de messages n'est pas encore tenu côté serveur,
 *   afficher un chiffre qu'on ne sait pas faire respecter est une promesse) ;
 * - il ne promet aucun remboursement (décision 5 : les CGV sont inchangées) ;
 * - il ne réclame pas de moyen de paiement, et le dit.
 */
export const TrialStart: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { canStart, isActive, hasUsed, starting, refusal, startTrial } =
    useSoloTrial();

  const [optIn, setOptIn] = useState(false);

  // `/dashboard`, et surtout PAS `/date` : `/date` n'est pas une route, c'est
  // le BASENAME sous lequel l'application est servie (cf. <BrowserRouter> de
  // date-polls). Y naviguer donnait un 404 dans les deux cas — en local il
  // tombait sur la route attrape-tout `/:handle` (« Profil non trouvé »), et en
  // production il aurait produit `/date/date`. Tout le reste du dépôt navigue
  // vers `/dashboard` ; c'était le seul endroit à s'en écarter. Trouvé le 18/09
  // en ouvrant réellement l'écran : aucune assertion serveur ne peut voir ça.
  const handleStart = async () => {
    const ok = await startTrial({ marketingOptIn: optIn });
    if (ok) navigate("/dashboard");
  };

  // `state.from` n'est pas décoratif : `/login` (AdminLoginPage) REPOSE
  // `auth_return_to` à partir de lui à chaque affichage, précisément pour
  // chasser le reliquat d'un flux précédent. Un `navigate("/login")` nu fait
  // donc retomber la destination sur "/" — et `signInWithGoogle` construit son
  // redirectTo à partir de cette clé. C'est le canal qu'AdminGuard emploie
  // déjà. Sans lui, quelqu'un arrivant d'un lien vers /essai se connecte et
  // atterrit sur l'accueil, sans jamais voir l'essai qu'on lui proposait.
  if (!user) {
    return (
      <EtatSimple
        titre={t("trial_start.signed_out.title")}
        texte={t("trial_start.signed_out.body")}
        ctaLabel={t("trial_start.signed_out.cta")}
        onCta={() => navigate("/login", { state: { from: location } })}
      />
    );
  }

  if (isActive) {
    return (
      <EtatSimple
        titre={t("trial_start.already_active.title")}
        texte={t("trial_start.already_active.body")}
        ctaLabel={t("trial_start.already_active.cta")}
        onCta={() => navigate("/dashboard")}
      />
    );
  }

  // `hasUsed` sans `isActive` : l'essai est derrière nous. On ne le repropose
  // pas — on propose l'abonnement, qui est ce qui reste.
  if (hasUsed || !canStart) {
    return (
      <EtatSimple
        titre={t("trial_start.unavailable.title")}
        texte={t("trial_start.unavailable.body")}
        ctaLabel={t("trial_start.unavailable.cta")}
        onCta={() => navigate("/pricing")}
      />
    );
  }

  return (
    <div className="min-h-screen-safe bg-background px-4 py-12">
      <div className="mx-auto max-w-2xl">
        <h1 className="text-3xl font-bold text-foreground">
          {t("trial_start.title")}
        </h1>
        <p className="mt-3 text-muted-foreground">
          {t("trial_start.subtitle")}
        </p>

        <ul className="mt-8 space-y-4" data-testid="trial-start-benefits">
          {AVANTAGES_PHARES.map(({ id, icon: Icon }) => (
            <li
              key={id}
              className="flex items-start gap-4"
              data-testid={`trial-benefit-${id}`}
            >
              <span className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
              </span>
              <span>
                <span className="block font-semibold text-foreground">
                  {t(`trial_start.benefit_${id}.title`)}
                </span>
                <span className="mt-0.5 block text-sm leading-relaxed text-muted-foreground">
                  {t(`trial_start.benefit_${id}.description`)}
                </span>
              </span>
            </li>
          ))}
        </ul>

        {/* Levées de contrainte, pas fonctionnalités : une ligne chacune, sans
            cadre. Leur donner le même poids que l'IA et les groupes diluait
            ces derniers. */}
        <ul
          className="mt-6 space-y-2 border-t border-border/60 pt-6"
          data-testid="trial-start-benefits-secondary"
        >
          {AVANTAGES_SECONDAIRES.map(({ id, icon: Icon }) => (
            <li
              key={id}
              className="flex items-start gap-3 text-sm text-muted-foreground"
              data-testid={`trial-benefit-secondary-${id}`}
            >
              <Icon
                className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <span>
                <span className="font-medium text-foreground">
                  {t(`trial_start.benefit_${id}.title`)}
                </span>
                {" — "}
                {t(`trial_start.benefit_${id}.description`)}
              </span>
            </li>
          ))}
        </ul>

        {/* Ce que devient le compte à la fin : la phrase qui évite qu'une
            personne inquiète supprime son compte en croyant tout perdre. Elle
            reste discrète — c'est une réassurance, pas un argument. */}
        <p className="mt-6 rounded-lg bg-muted/40 p-4 text-sm leading-relaxed text-muted-foreground">
          {t("trial_start.what_happens_after")}
        </p>

        {/* La case vit AVANT le bouton et en est visuellement détachée : elle
            ne conditionne pas l'essai, et le bouton ne doit pas se lire comme
            un consentement à la prospection. */}
        <label className="mt-8 flex cursor-pointer items-start gap-3 rounded-lg border border-border/60 bg-muted/30 p-4">
          <input
            type="checkbox"
            checked={optIn}
            onChange={(e) => setOptIn(e.target.checked)}
            className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-border"
            data-testid="trial-start-marketing-opt-in"
          />
          <span className="text-sm text-muted-foreground">
            {t("trial_start.marketing_opt_in")}
          </span>
        </label>

        <button
          type="button"
          onClick={handleStart}
          disabled={starting}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
          data-testid="trial-start-cta"
        >
          {starting ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          )}
          {t("trial_start.cta")}
        </button>

        <p className="mt-3 text-center text-xs text-muted-foreground">
          {t("trial_start.no_card")}
        </p>

        {refusal && (
          <p
            role="alert"
            className="mt-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
            data-testid="trial-start-refusal"
          >
            {t(`trial_start.refusal.${refusal}`)}
          </p>
        )}
      </div>
    </div>
  );
};

const EtatSimple: React.FC<{
  titre: string;
  texte: string;
  ctaLabel: string;
  onCta: () => void;
}> = ({ titre, texte, ctaLabel, onCta }) => (
  <div className="min-h-screen-safe bg-background px-4 py-12">
    <div className="mx-auto max-w-2xl text-center">
      <h1 className="text-2xl font-bold text-foreground">{titre}</h1>
      <p className="mt-3 text-muted-foreground">{texte}</p>
      <button
        type="button"
        data-testid="trial-start-cta"
        onClick={onCta}
        className="mt-8 inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        {ctaLabel}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  </div>
);

export default TrialStart;
